import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const ContactLawyer = () => {
  const [message, setMessage] = useState('');
  const [contactMethod, setContactMethod] = useState('platform');
  const [urgency, setUrgency] = useState('standard');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  const { lawyer, formData } = location.state || {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitted(true);
    setIsSubmitting(false);
  };

  const handleBackToMatches = () => {
    navigate('/lawyer-matching', { state: { formData } });
  };

  const handleGoToPortal = () => {
    navigate('/client-portal');
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-surface rounded-lg shadow-card p-8">
            <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle" size={32} className="text-success" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-text-primary mb-2">
              Message Sent Successfully!
            </h2>
            <p className="text-text-secondary mb-6">
              Your message has been sent to {lawyer?.name}. They will respond within {lawyer?.responseTime?.toLowerCase()}.
            </p>
            <div className="space-y-3">
              <button
                onClick={handleGoToPortal}
                className="w-full bg-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 transition-smooth"
              >
                Go to Client Portal
              </button>
              <button
                onClick={handleBackToMatches}
                className="w-full border border-border text-text-primary py-3 px-4 rounded-lg font-medium hover:bg-secondary-50 transition-smooth"
              >
                View Other Lawyers
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!lawyer) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-warning-600 mx-auto mb-4" />
          <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">
            Lawyer information not found
          </h2>
          <p className="text-text-secondary mb-4">
            Please go back and select a lawyer to contact
          </p>
          <button
            onClick={() => navigate('/lawyer-matching')}
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-smooth"
          >
            Back to Lawyer Matches
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Scale" size={20} color="white" />
              </div>
              <div>
                <span className="text-xl font-heading font-bold text-primary">LawDesk KE</span>
                <p className="text-sm text-text-secondary">Contact Lawyer</p>
              </div>
            </div>
            <button
              onClick={handleBackToMatches}
              className="text-text-secondary hover:text-text-primary transition-smooth"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Lawyer Information */}
            <div className="lg:col-span-1">
              <div className="bg-surface rounded-lg shadow-card p-6 sticky top-8">
                <div className="text-center mb-6">
                  <img 
                    src={lawyer?.image} 
                    alt={lawyer?.name}
                    className="w-24 h-24 rounded-full object-cover mx-auto mb-4"
                  />
                  <h3 className="text-xl font-heading font-semibold text-text-primary mb-1">
                    {lawyer?.name}
                  </h3>
                  <p className="text-primary font-medium mb-2">{lawyer?.specialization}</p>
                  <div className="flex items-center justify-center space-x-1 mb-2">
                    <Icon name="Star" size={16} className="text-warning-500 fill-current" />
                    <span className="text-sm font-medium text-text-primary">{lawyer?.rating}</span>
                    <span className="text-sm text-text-secondary">({lawyer?.reviews} reviews)</span>
                  </div>
                  <p className="text-sm text-text-secondary">{lawyer?.location}</p>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-text-secondary">Experience</span>
                    <span className="text-sm font-medium text-text-primary">{lawyer?.experience} years</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-text-secondary">Fees</span>
                    <span className="text-sm font-medium text-text-primary">{lawyer?.fees}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-text-secondary">Response Time</span>
                    <span className="text-sm font-medium text-success">{lawyer?.responseTime}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-text-secondary">Success Rate</span>
                    <span className="text-sm font-medium text-success">{lawyer?.successRate}%</span>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-border">
                  <h4 className="text-sm font-medium text-text-primary mb-2">Languages</h4>
                  <div className="flex flex-wrap gap-2">
                    {lawyer?.languages?.map(lang => (
                      <span key={lang} className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full">
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <h4 className="text-sm font-medium text-text-primary mb-2">Credentials</h4>
                  <div className="space-y-1">
                    {lawyer?.credentials?.map(credential => (
                      <div key={credential} className="flex items-center space-x-2">
                        <Icon name="CheckCircle" size={14} className="text-success" />
                        <span className="text-xs text-text-secondary">{credential}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-surface rounded-lg shadow-card p-6">
                <div className="mb-6">
                  <h2 className="text-2xl font-heading font-bold text-text-primary mb-2">
                    Contact {lawyer?.name?.split(' ')[1] || lawyer?.name}
                  </h2>
                  <p className="text-text-secondary">
                    Send a message to discuss your legal matter. Your information will be kept confidential.
                  </p>
                </div>

                {/* Case Summary */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
                  <h3 className="font-medium text-primary mb-2">Your Legal Matter Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-text-secondary">Category:</span>
                      <span className="ml-2 font-medium text-text-primary">{formData?.legalCategory}</span>
                    </div>
                    <div>
                      <span className="text-text-secondary">Urgency:</span>
                      <span className="ml-2 font-medium text-text-primary">{formData?.urgency}</span>
                    </div>
                    {formData?.budget && (
                      <div className="md:col-span-2">
                        <span className="text-text-secondary">Budget:</span>
                        <span className="ml-2 font-medium text-text-primary">{formData?.budget}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-3">
                    <span className="text-text-secondary text-sm">Description:</span>
                    <p className="mt-1 text-text-primary text-sm">{formData?.description}</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Additional Message (Optional)
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target?.value)}
                      rows={4}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Any additional information or specific questions you'd like to discuss..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Preferred Contact Method
                    </label>
                    <select
                      value={contactMethod}
                      onChange={(e) => setContactMethod(e.target?.value)}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="platform">Platform Messaging</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone Call</option>
                      <option value="video">Video Call</option>
                      <option value="in-person">In-Person Meeting</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Response Urgency
                    </label>
                    <select
                      value={urgency}
                      onChange={(e) => setUrgency(e.target?.value)}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="immediate">Immediate Response Needed</option>
                      <option value="urgent">Urgent (Within 24 hours)</option>
                      <option value="standard">Standard (Within 48 hours)</option>
                      <option value="flexible">Flexible Timeline</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="terms"
                      required
                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                    />
                    <label htmlFor="terms" className="text-sm text-text-secondary">
                      I agree to the <a href="#" className="text-primary hover:underline">Terms of Service</a> and <a href="#" className="text-primary hover:underline">Privacy Policy</a>
                    </label>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      type="button"
                      onClick={handleBackToMatches}
                      className="flex items-center justify-center space-x-2 px-6 py-3 border border-border text-text-primary rounded-lg hover:bg-secondary-50 transition-smooth"
                    >
                      <Icon name="ArrowLeft" size={16} />
                      <span>Back to Matches</span>
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 flex items-center justify-center space-x-2 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
                    >
                      {isSubmitting ? (
                        <>
                          <Icon name="Loader2" size={16} className="animate-spin" />
                          <span>Sending Message...</span>
                        </>
                      ) : (
                        <>
                          <Icon name="Send" size={16} />
                          <span>Send Message</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactLawyer;